<template>
  <section
    class="accruals-rules-builder"
    :class="{ 'accruals-rules-builder--embedded': embedded }"
    data-testid="accruals-configuration-page__rules-builder"
  >
    <article class="accruals-rules-builder__card">
      <header v-if="!embedded" class="accruals-rules-builder__header">
        <div class="accruals-rules-builder__heading">
          <h2 class="accruals-rules-builder__title">Conditions</h2>
          <p class="accruals-rules-builder__subtitle">{{ subtitle }}</p>
        </div>

        <div v-if="editing" class="accruals-rules-builder__header-actions">
          <base-button
            secondary
            label="Cancel"
            class="accruals-rules-builder__cancel-btn"
            :disabled="saving"
            data-testid="accruals-configuration-page__rules-cancel-btn"
            @click="$emit('cancel')"
          />
          <base-button
            primary
            label="Save"
            class="accruals-rules-builder__save-btn"
            :loading="saving"
            :disabled="saving"
            data-testid="accruals-configuration-page__rules-save-btn"
            @click="$emit('save')"
          />
        </div>

        <base-button
          v-else
          secondary
          label="Edit"
          class="accruals-rules-builder__edit-btn"
          :image="assets.ICON_EDIT"
          image-alt=""
          data-testid="accruals-configuration-page__rules-edit-btn"
          @click="$emit('edit')"
        />
      </header>

      <header v-else class="accruals-rules-builder__embedded-header">
        <div class="accruals-rules-builder__heading">
          <h2 class="accruals-rules-builder__title">{{ title }}</h2>
          <p class="accruals-rules-builder__subtitle">{{ resolvedSubtitle }}</p>
        </div>
        <button
          v-if="editing"
          type="button"
          class="accruals-rules-builder__add-link"
          :disabled="saving"
          data-testid="accruals-configuration-page__add-condition-btn"
          @click="$emit('add-condition')"
        >
          <span class="accruals-rules-builder__add-icon-wrap">
            <img :src="assets.ICON_PLUS_DARK" alt="" class="accruals-rules-builder__add-icon" />
          </span>
          <span>Add condition</span>
        </button>
      </header>

      <div class="accruals-rules-builder__panel">
        <div class="accruals-rules-builder__list">
          <div
            v-for="(condition, index) in conditions"
            :key="condition.id"
            class="accruals-rules-builder__condition-block"
          >
            <accruals-condition-row
              :condition="condition"
              :index="index"
              :field-items="fieldItems"
              :editing="editing"
              :saving="saving"
              :errors="conditionErrors[index] || emptyErrors"
              :data-testid="`accruals-configuration-page__condition-row--${index}`"
              @update="onUpdateCondition(index, $event)"
              @delete="$emit('delete-condition', index)"
            />

            <accruals-condition-connector
              v-if="index < conditions.length - 1"
              :value="connectors[index]"
              :editable="editing"
              :data-testid="`accruals-configuration-page__condition-connector--${index}`"
              @input="onConnectorChange(index, $event)"
            />
          </div>
        </div>

        <button
          v-if="editing && !embedded"
          type="button"
          class="accruals-rules-builder__add-link"
          :disabled="saving"
          data-testid="accruals-configuration-page__add-condition-btn"
          @click="$emit('add-condition')"
        >
          <span class="accruals-rules-builder__add-icon-wrap">
            <img :src="assets.ICON_PLUS_DARK" alt="" class="accruals-rules-builder__add-icon" />
          </span>
          <span>Add condition</span>
        </button>

        <div
          v-if="!embedded"
          class="accruals-rules-builder__divider"
          aria-hidden="true"
        />

        <div
          v-if="!embedded"
          class="accruals-rules-builder__custom-pattern"
          :class="{ 'accruals-rules-builder__custom-pattern--boxed': embedded }"
        >
          <div class="accruals-rules-builder__custom-pattern-header">
            <div class="accruals-rules-builder__custom-pattern-copy">
              <p class="accruals-rules-builder__custom-pattern-title">Custom Pattern</p>
              <p class="accruals-rules-builder__custom-pattern-subtitle">
                Define a custom logical expression
              </p>
            </div>
            <base-switch
              v-if="editing"
              :value="ruleMeta.useCustomPattern"
              :disabled="saving"
              aria-label="Custom pattern"
              data-testid="accruals-configuration-page__custom-pattern-toggle"
              @input="onCustomPatternToggle"
            />
          </div>

          <v-text-field
            v-if="editing && ruleMeta.useCustomPattern"
            :value="ruleMeta.customPattern"
            dense
            outlined
            hide-details="auto"
            placeholder="e.g. (1 AND 2) OR 3"
            class="accruals-rules-builder__pattern-input"
            data-testid="accruals-configuration-page__custom-pattern-input"
            @input="onCustomPatternInput"
          />

          <div
            v-else-if="!editing && displayedPattern"
            class="accruals-rules-builder__pattern-readout"
            data-testid="accruals-configuration-page__custom-pattern-readout"
          >
            {{ displayedPattern }}
          </div>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import AccrualsConditionRow from './AccrualsConditionRow.vue';
import AccrualsConditionConnector from './AccrualsConditionConnector.vue';
import {
  ACCRUALS_CONFIG_ASSETS,
  RULES_BUILDER_SUBTITLE,
  getConditionFieldsForTab,
} from '../accrualsConfigurationConstants';
import {
  buildLinearPattern,
  getDisplayedPattern,
  normalizeRuleMeta,
} from '../accrualsRulesBuilderUtils';

export default {
  name: 'AccrualsRulesBuilder',
  components: {
    AccrualsConditionRow,
    AccrualsConditionConnector,
  },
  props: {
    tabId: { type: String, default: 'estimation-rules' },
    conditions: { type: Array, default: () => [] },
    ruleMeta: {
      type: Object,
      default: () => ({
        connectors: [],
        useCustomPattern: false,
        customPattern: '',
      }),
    },
    editing: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    conditionErrors: { type: Object, default: () => ({}) },
    embedded: { type: Boolean, default: false },
    title: { type: String, default: 'Conditions' },
    subtitle: { type: String, default: '' },
    subtitleOverride: { type: String, default: '' },
    fieldItemsOverride: { type: Array, default: () => [] },
  },
  data() {
    return {
      assets: ACCRUALS_CONFIG_ASSETS,
      emptyErrors: { field: '', operator: '', value: '' },
    };
  },
  computed: {
    resolvedSubtitle() {
      return this.subtitle || this.subtitleOverride || RULES_BUILDER_SUBTITLE;
    },
    fieldItems() {
      if (this.fieldItemsOverride?.length) return this.fieldItemsOverride;
      return getConditionFieldsForTab(this.tabId);
    },
    connectors() {
      return normalizeRuleMeta(this.ruleMeta, this.conditions.length).connectors;
    },
    displayedPattern() {
      return getDisplayedPattern(this.ruleMeta, this.conditions.length);
    },
  },
  methods: {
    onUpdateCondition(index, nextCondition) {
      this.$emit('update-condition', { index, condition: nextCondition });
    },
    onConnectorChange(index, value) {
      const nextConnectors = this.connectors.slice();
      nextConnectors[index] = value;
      this.emitRuleMeta({
        connectors: nextConnectors,
        customPattern: buildLinearPattern(nextConnectors, this.conditions.length),
      });
    },
    onCustomPatternToggle(enabled) {
      this.emitRuleMeta({
        useCustomPattern: Boolean(enabled),
        customPattern: this.displayedPattern,
      });
    },
    onCustomPatternInput(value) {
      this.emitRuleMeta({
        useCustomPattern: true,
        customPattern: value,
      });
    },
    emitRuleMeta(patch) {
      this.$emit('update-rule-meta', {
        ...normalizeRuleMeta(this.ruleMeta, this.conditions.length),
        ...patch,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsRulesBuilder.scss';
</style>
